import { Container, Text, TextStyle } from 'pixi.js';
import { GAME_VERSION } from '@wooorms/shared';
import { UIScaleManager } from '../utils/UIScaleManager';

// Re-export for backward compatibility
export { GAME_VERSION };

/**
 * VersionDisplay configuration
 */
export interface VersionDisplayConfig {
  /** Padding from edge of screen */
  padding: number;
  /** Text alpha */
  alpha: number;
}

const DEFAULT_CONFIG: VersionDisplayConfig = {
  padding: 10,
  alpha: 0.4,
};

/**
 * VersionDisplay - Small, unobtrusive version text at bottom-right corner
 *
 * Shows current game version without interfering with gameplay
 */
export class VersionDisplay {
  /** Main container */
  public container: Container;

  /** Version text */
  private versionText: Text;

  /** Configuration */
  private config: VersionDisplayConfig;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  constructor(config: Partial<VersionDisplayConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();
    this.container = new Container();
    this.container.label = 'VersionDisplay';

    // Create version text
    this.versionText = new Text({
      text: `v${GAME_VERSION}`,
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: this.scaleManager.scaleFontSize(10),
        fontWeight: 'normal',
        fill: 0x888888,
        letterSpacing: 0.5,
      }),
    });
    this.versionText.anchor.set(1, 1); // Bottom-right anchor
    this.container.addChild(this.versionText);

    // Set initial alpha
    this.container.alpha = this.config.alpha;

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());
  }

  /**
   * Apply current scaling to all elements
   */
  private applyScaling(): void {
    (this.versionText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(10);
  }

  /**
   * Update position when screen is resized
   */
  onResize(width: number, height: number): void {
    const scaledPadding = this.scaleManager.scaleSpacing(this.config.padding);
    this.container.x = width - scaledPadding;
    this.container.y = height - scaledPadding;
  }

  /**
   * Show the version display
   */
  show(): void {
    this.container.visible = true;
  }

  /**
   * Hide the version display
   */
  hide(): void {
    this.container.visible = false;
  }

  /**
   * Get current version string
   */
  getVersion(): string {
    return GAME_VERSION;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.versionText.destroy();
    this.container.destroy({ children: true });
  }
}
